import { Injectable, signal, inject } from '@angular/core';
import { HeaderAiService } from '../services/header-ai.service';
import { SidebarMenuItem, UserProfile, ModuleName } from '../enums/header-ai.model';
import { AuthService } from '../../../../auth/auth.service';

@Injectable()

export class HeaderAiStore {
  private headerService = inject(HeaderAiService);
  private authService = inject(AuthService);

  // UI state stored as signals
  moduleName = signal<ModuleName>('VMS');
  sidebarMenu = signal<SidebarMenuItem[]>([]);
  userProfile = signal<UserProfile | null>(null);
  orgUnits = signal<any[]>([]);
  firstname = signal<string>('');
  lastname = signal<string>('');
  userId = signal<string>('');
  allowExpenseModule = signal<boolean>(false);
  // dropdowns
  isAppsOpen = signal<boolean>(false);
  isProfileOpen = signal<boolean>(false);
  // search
  searchQuery = signal<string>('');


  init(): void {
    // initialize menu and flags
    const items = this.headerService.getMenuItems() || [];
    this.sidebarMenu.set(items);
    this.allowExpenseModule.set(this.headerService.isPlantAllowed());

    // load profile
    this.headerService.getProfile().subscribe({
      next: (data) => {
        if (data) {
          const currentProfile = JSON.parse(localStorage.getItem('userProfile') || '{}');
          const serverRoles = (data as any)?.roles;
          const updatedProfile: UserProfile = {
            ...currentProfile,
            ...data,
            roles: serverRoles
              ? {
                  ...(currentProfile?.roles || {}),
                  ...serverRoles,
                  auth_items: serverRoles.auth_items || [],
                  module_names: serverRoles.module_names || []
                }
              : currentProfile?.roles
          };
          localStorage.setItem('userProfile', JSON.stringify(updatedProfile));

          this.userProfile.set(updatedProfile);
          this.firstname.set(updatedProfile.profile?.firstname || updatedProfile.username || '');
          this.lastname.set(updatedProfile.profile?.lastname || '');
          this.userId.set(updatedProfile.user_id || '');
          const units = (updatedProfile as any).org_units || [];
          this.orgUnits.set(units);
          this.headerService.setOrgUnits(units);
          
          this.authService.getPublicConfig().subscribe();

          this.headerService.refreshMenuItems();
        }
      },
      error: () => {
        // swallow here; component can react if needed
      },
    });
  }

  setSearchQuery(q: string) {
    this.searchQuery.set(q);
  }

  toggleApps() {
    this.isAppsOpen.update(v => !v);
    if (this.isAppsOpen()) {
      this.isProfileOpen.set(false);
    } else {
      // when apps menu is closed, clear search
      this.searchQuery.set('');
    }
  }

  toggleProfile() {
    this.isProfileOpen.update(v => !v);
    if (this.isProfileOpen()) {
      this.isAppsOpen.set(false);
    }
  }

  closeAll() {
    this.isAppsOpen.set(false);
    this.isProfileOpen.set(false);
    // clear search on close
    this.searchQuery.set('');
  }

  updateModuleName(url: string) {
    if (url.startsWith('/vms')) this.moduleName.set('VMS');
    else if (url.startsWith('/admin')) this.moduleName.set('Admin');
    else if (url.startsWith('/account')) this.moduleName.set('Account');
    else if (url.startsWith('/grievance')) this.moduleName.set('Grievance');
    else if (url.startsWith('/ai-buddy')) this.moduleName.set('AI Buddy');
    else if (url.startsWith('/expense')) this.moduleName.set('Trip And Expense');
    else if (url.startsWith('/conference-room')) this.moduleName.set('Conference Room');
    else if (url.startsWith('/approval-notes')) this.moduleName.set('Approval Notes');
    else this.moduleName.set('Welcome');
  }

  getFirstAccessibleRoute(module: string): string {
    const moduleRoutes: { [key: string]: { permission: string; route: string }[] } = {
      'vms': [
        { permission: 'vms-dashboard', route: '/vms/dashboard' },
        { permission: 'vms-frontdesk', route: '/vms/frontdesk' },
        { permission: 'vms-invitation', route: '/vms/invitations' },
        { permission: 'vms-visitors', route: '/vms/visitors' },
        { permission: 'vms-feedbackreports', route: '/vms/reports/feedback-report' },
        { permission: 'vms-visitorreport', route: '/vms/reports/visitor-report' },
        { permission: 'vms-checkoutreport', route: '/vms/reports/pending-checkout-report' },
        { permission: 'vms-force-out-report', route: '/vms/reports/force-checkout-report' },
        { permission: 'vms-timespentreport', route: '/vms/reports/time-spent-report' },
        { permission: 'raise-grievance-access', route: '/grievance/employee/portal' }
      ],
      'admin': [
        { permission: 'admin-users', route: '/admin/users' },
        { permission: 'admin-roles', route: '/admin/roles-permissions' },
        { permission: 'admin-orgunit', route: '/admin/organizations' },
        { permission: 'admin-desk', route: '/admin/desks' }
      ],
      'account': [
        { permission: 'account-myaccount', route: '/account/my-account' }
      ],
      'grievance': [
        { permission: 'grievance-approval', route: '/grievance/dashboard' },
        { permission: 'grievance-approval', route: '/grievance/request' },
        { permission: 'grievance-settings', route: '/grievance/settings' },
        { permission: 'grievance-report', route: '/grievance/grievance-report' },
        { permission: 'escalation-report', route: '/grievance/escalation-report' },
      ],
      'expense': [
        { permission: '', route: '/expense/dashboard' },
        { permission: 'expense-my-trip', route: '/expense/my-trip' },
        { permission: 'expense-travel-desk', route: '/expense/travel-desk' },
        { permission: 'expense-settings', route: '/expense/settings/expense-types' },
        { permission: 'expense-settings', route: '/expense/settings/expense-approval-matrix' },
        { permission: 'expense-settings', route: '/expense/settings/gl-master' },
        { permission: 'expense-settings', route: '/expense/settings/cost-center' },
        { permission: 'expense-settings', route: '/expense/settings/travel-policy' },
        { permission: 'expense-settings', route: '/expense/settings/project-base' },
        { permission: 'expense-settings', route: '/expense/settings/travel-desk-master' },
        { permission: 'expense-approvals', route: '/expense/trip-approvals' },
        { permission: 'expense-reimbursement', route: '/expense/my-reimbursement' },
        { permission: 'expense-reimbursement-approval', route: '/expense/reimbursement-approval' },
        { permission: 'expense-dashboard', route: '/expense/dashboard' },
        { permission: 'expense-trip-history', route: '/expense/reports/trip-history' },
        { permission: 'expense-trip-roster', route: '/expense/reports/trip-roster' },
        { permission: 'expense-claimsummary', route: '/expense/reports/claim-summary' },
      ],
      'conference-room': [
        { permission: '', route: '/conference-room/dashboard' },
        { permission: '', route: '/conference-room/my-bookings' },
        { permission: 'cr-room-display', route: '/conference-room/room-display' },
        { permission: 'cr-approvals', route: '/conference-room/approvals' },
        { permission: 'cr-reports', route: '/conference-room/reports/room-utilisation' },
        { permission: 'cr-reports', route: '/conference-room/reports/approval-tracking' },
        { permission: 'cr-reports', route: '/conference-room/reports/booking-summary' },
        { permission: 'cr-amenities', route: '/conference-room/settings/amenities' },
        { permission: 'cr-conference-room-master', route: '/conference-room/settings/conference-rooms' },
      ],
      'ai-buddy': [
        { permission: '', route: '/ai-buddy/conversations' },
        { permission: '', route: '/ai-buddy/alerts' },
        { permission: '', route: '/ai-buddy/employees' }
      ],
      'approval-notes': [
        { permission: '', route: '/approval-notes/dashboard' },
        { permission: 'approval-notes-budget-approval-display', route: '/approval-notes/budget-approval' },
        { permission: 'approval-notes-vendor-ticket-display', route: '/approval-notes/vendor-ticket' },
        { permission: 'approval-notes-vendor-selection-display', route: '/approval-notes/vendor-selection' },
        { permission: 'approval-notes-scrap-approval-display', route: '/approval-notes/scrap-approval' },
        { permission: 'approval-notes-vendor-creation-display', route: '/approval-notes/vendor-creation' },
        { permission: 'approval-notes-payment-initiate-display', route: '/approval-notes/payment-approval' },
        { permission: 'approval-notes-vendors-display', route: '/approval-notes/vendors' },
      ]
    };

    if (module === 'vms') return '/vms/dashboard';
    if (module === 'expense') return '/expense/dashboard';
    if (module === 'conference-room') return '/conference-room/dashboard';
    if (module === 'approval-notes') return '/approval-notes/dashboard';
    if (module === 'ai-buddy') return '/ai-buddy/conversations';

    const permissions = this.headerService.getUserPermissions();
    const routes = moduleRoutes[module];

    if (!routes) return '/';

    if (this.headerService.isAdmin()) {
      return routes.length > 0 ? routes[0].route : '/';
    }

    const firstAccessibleRoute = routes.find(route => permissions.includes(route.permission));

    return firstAccessibleRoute ? firstAccessibleRoute.route : '/';
  }
}
